import { DEFAULT_FEATURE_FLAGS } from "models/interfaces/feature-flags";
import FeatureFlagRecord from "models/view-models/feature-flag-record";
import ScopedFeatureFlagRecord from "models/view-models/scoped-feature-flag-record";
import { useCallback, useState } from "react";
import { FeatureFlagScopeType } from "utilities/enumerations/feature-flag-scope-type";
import FeatureFlagsService from "utilities/services/feature-flags/feature-flags-service";
import ScopedFeatureFlagsService from "utilities/services/feature-flags/scoped-feature-flags-service";
import { ToastManager } from "utilities/toast/toast-manager";

export default function useListFeatureFlags() {
    const [isLoading, setIsLoading] = useState(false);
    const [featureFlags, setFeatureFlags] = useState<FeatureFlagRecord[]>(
        Object.entries(DEFAULT_FEATURE_FLAGS).map(
            ([name, isEnabled]) => new FeatureFlagRecord({ name, isEnabled })
        )
    );
    const [scopedFeatureFlags, setScopedFeatureFlags] = useState<
        ScopedFeatureFlagRecord[]
    >([]);

    const listFeatureFlags = useCallback(async () => {
        setIsLoading(true);

        try {
            const { resultObjects } = await FeatureFlagsService.list();

            const flags = resultObjects.map(
                (flag) => new FeatureFlagRecord(flag)
            );
            setFeatureFlags(flags);
        } catch (err) {
            ToastManager.error(
                err instanceof Error ? err.message : "An error occurred"
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    const listScopedFeatureFlags = useCallback(
        async (scopeType: FeatureFlagScopeType) => {
            setIsLoading(true);
            try {
                const { resultObjects } = await ScopedFeatureFlagsService.list({
                    scopeType: scopeType,
                });
                const flags = resultObjects.map(
                    (flag) => new ScopedFeatureFlagRecord(flag)
                );
                setScopedFeatureFlags(flags);
            } catch (err) {
                ToastManager.error(
                    err instanceof Error ? err.message : "An error occurred"
                );
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    return {
        featureFlags,
        scopedFeatureFlags,
        isLoading,
        listFeatureFlags,
        listScopedFeatureFlags,
    };
}
